import type { AnalyticsActor, AnalyticsSurface, PaymentOptionType } from './types';

/** Canonical PostHog event names — single source of truth. */
export const ANALYTICS_EVENTS = {
  // Identity / verify
  enroll_verify_submitted: 'enroll_verify_submitted',
  enroll_verify_succeeded: 'enroll_verify_succeeded',
  enroll_verify_failed: 'enroll_verify_failed',

  // Flow navigation
  flow_step_viewed: 'flow_step_viewed',

  // Step 4 unlock
  next_steps_unlocked: 'next_steps_unlocked',

  // Checkout
  checkout_viewed: 'checkout_viewed',
  payment_option_selected: 'payment_option_selected',

  // Payment schedule
  payment_schedule_created: 'payment_schedule_created',
  payment_schedule_confirmed: 'payment_schedule_confirmed',

  // Payment method
  payment_method_saved: 'payment_method_saved',

  // Financing
  financing_application_submitted: 'financing_application_submitted',
  financing_application_failed: 'financing_application_failed',

  // Documents
  reso_docs_generated: 'reso_docs_generated',
  signing_link_sent: 'signing_link_sent',
  document_signing_started: 'document_signing_started',
  document_signing_completed: 'document_signing_completed',

  // Enrollment completion
  enrollment_completed: 'enrollment_completed',
  enrollment_completion_failed: 'enrollment_completion_failed',

  // Rep operations
  rep_product_updated: 'rep_product_updated',
  rep_session_started: 'rep_session_started',
  rep_session_ended: 'rep_session_ended',

  // Error surfaces
  error_screen_shown: 'error_screen_shown',
} as const;

export type AnalyticsEventName = (typeof ANALYTICS_EVENTS)[keyof typeof ANALYTICS_EVENTS];

export type EventPayloadMap = {
  enroll_verify_submitted: { opportunityId: string };
  enroll_verify_succeeded: { opportunityId: string; accountId: string };
  enroll_verify_failed: { opportunityId: string; reason?: string };
  flow_step_viewed: { opportunityId: string; step: number; stepKey: string; sceneKey?: string };
  next_steps_unlocked: { opportunityId: string; unlockedBy: 'rep' | 'client' };
  checkout_viewed: { opportunityId: string };
  payment_option_selected: {
    opportunityId: string;
    paymentOption: PaymentOptionType;
    selectedBy: AnalyticsActor;
  };
  payment_schedule_created: { opportunityId: string; paymentCount: number; totalAmount: number };
  payment_schedule_confirmed: { opportunityId: string; paymentCount: number };
  payment_method_saved: { opportunityId: string; methodType: 'credit_card' | 'ach' | 'plaid' };
  financing_application_submitted: { opportunityId: string };
  financing_application_failed: { opportunityId: string; reason?: string };
  reso_docs_generated: { opportunityId: string; docType: 'reso' | 'fsa'; outcome: string };
  signing_link_sent: { opportunityId: string; channel: 'sms' | 'email' };
  document_signing_started: { opportunityId: string; documentId?: string };
  document_signing_completed: { opportunityId: string; documentId?: string };
  enrollment_completed: { opportunityId: string; accountId?: string };
  enrollment_completion_failed: { opportunityId: string; errorCode?: string; reason?: string };
  rep_product_updated: { opportunityId: string; action: 'add' | 'update' | 'delete' };
  rep_session_started: { opportunityId: string; sessionId?: string };
  rep_session_ended: { opportunityId: string; sessionId?: string };
  error_screen_shown: { opportunityId?: string; title?: string; surface: AnalyticsSurface };
};

export type TrackContext = {
  actor: AnalyticsActor;
  surface: AnalyticsSurface;
  opportunityId?: string;
  accountId?: string;
  repId?: string;
};

export type TrackEventInput<E extends AnalyticsEventName = AnalyticsEventName> = {
  event: E;
  properties: E extends keyof EventPayloadMap ? EventPayloadMap[E] : Record<string, unknown>;
  context?: Partial<TrackContext>;
};
